/**
 * Cart Drawer Recommendations
 * ----------------------------------------------------------------------------
 * Loaded by sections/cart-drawer.liquid only when the section setting
 * `enable_cart_recommendations` is true. Disabling that setting prevents this
 * file from ever being requested.
 *
 * Interaction model
 *   1. Card default state shows image · name · price · ADD TO CART
 *   2. Tapping ADD TO CART reveals a size picker overlaid on the image bottom
 *   3. Tapping a size adds that variant straight to the cart
 *      (per-button spinner; success closes the picker)
 *
 * Runtime dependencies (provided by maceoo-custom.js):
 *   • window.maceooAddToCart(variantId)  → Promise<cartContent>
 *   • window.maceooShowToast(message, isError)
 */

(function () {
  "use strict";

  if (window.__maceooCartRec) return;
  window.__maceooCartRec = true;

  /* --- config ------------------------------------------------------------ */

  // Cache prefix is versioned so a markup change invalidates stale entries.
  var CACHE_PREFIX = "mc-rec:v3:";
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
  var REC_SECTION  = "cart-drawer-recommendations";
  var MAX_DESKTOP  = 5;
  var MAX_MOBILE   = 10;

  /* --- analytics --------------------------------------------------------- */

  // Reset on each drawer-open so view fires exactly once per user-perceived
  // session of the cart drawer. Refreshes within one open will not refire.
  var viewLoggedThisOpen = false;

  function track(eventName, params) {
    if (typeof window.maceooTrack === "function") {
      window.maceooTrack(eventName, params || {});
    }
  }

  function getCardMeta(card) {
    if (!card) return {};
    var titleEl = card.querySelector(".cart-rec-card__title");
    var priceEl = card.querySelector(".cart-rec-card__price");
    var parent  = card.parentElement;
    var position = parent ? Array.prototype.indexOf.call(parent.children, card) + 1 : 0;
    return {
      product_id:     card.dataset.productId     || "",
      product_handle: card.dataset.productHandle || "",
      product_title:  titleEl ? titleEl.textContent.trim() : "",
      price:          priceEl ? priceEl.textContent.trim() : "",
      position:       position,
      surface:        isDesktop() ? "desktop" : "mobile",
    };
  }

  /* --- DOM lookups ------------------------------------------------------- */

  var isDesktop          = function () { return window.matchMedia("(min-width: 1024px)").matches; };
  var getPanel           = function () { return document.getElementById("cart-recommendations-panel"); };
  var getDesktopList     = function () { return document.getElementById("cart-rec-list-desktop"); };
  var getMobileContainer = function () { return document.getElementById("cart-rec-mobile"); };
  var getCartDrawer      = function () { return document.getElementById("cart-drawer"); };

  /* --- cache ------------------------------------------------------------- */

  function readCache(sourceId) {
    if (!sourceId) return null;
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + sourceId);
      if (!raw) return null;
      var data = JSON.parse(raw);
      return Date.now() - data.ts > CACHE_TTL_MS ? null : data.html;
    } catch (_e) {
      return null;
    }
  }

  function writeCache(sourceId, html) {
    if (!sourceId) return;
    try {
      sessionStorage.setItem(
        CACHE_PREFIX + sourceId,
        JSON.stringify({ ts: Date.now(), html: html }),
      );
    } catch (_e) {
      /* sessionStorage may be full or disabled — caching is a nice-to-have */
    }
  }

  /* --- fetching (with in-flight de-dupe) --------------------------------- */

  var inFlight = null;

  function fetchRecommendations(productId) {
    if (!productId) return Promise.reject(new Error("No source product"));

    var cached = readCache(productId);
    if (cached) return Promise.resolve(cached);

    if (inFlight && inFlight.productId === productId) return inFlight.promise;

    var fetchLimit = Math.max(MAX_DESKTOP, MAX_MOBILE) + 6;
    var url =
      window.Shopify.routes.root +
      "recommendations/products?section_id=" + REC_SECTION +
      "&product_id=" + productId +
      "&limit=" + fetchLimit;

    var promise = fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error("Recs fetch failed");
        return res.text();
      })
      .then(function (html) {
        writeCache(productId, html);
        return html;
      })
      .finally(function () {
        if (inFlight && inFlight.productId === productId) inFlight = null;
      });

    inFlight = { productId: productId, promise: promise };
    return promise;
  }

  /* --- render ------------------------------------------------------------ */

  function parseRecommendations(html, cartIds, limit) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var wrapper = doc.querySelector(".cart-rec-section-wrapper");
    if (!wrapper) return [];
    return Array.from(wrapper.querySelectorAll(".cart-rec__item"))
      .filter(function (el) { return cartIds.indexOf(el.dataset.productId) === -1; })
      .slice(0, limit);
  }

  function fillContainer(container, items, clone) {
    container.innerHTML = "";
    items.forEach(function (item) {
      container.appendChild(clone ? item.cloneNode(true) : item);
    });
  }

  function renderRecommendations(html, cartIds) {
    var desktopCount = 0;
    var mobileCount  = 0;

    if (isDesktop()) {
      var list = getDesktopList();
      if (list) {
        var items = parseRecommendations(html, cartIds, MAX_DESKTOP);
        desktopCount = items.length;
        if (items.length) fillContainer(list, items, false);
        else              list.innerHTML = "";
      }
    }

    var mobile = getMobileContainer();
    if (mobile) {
      var mobileList = mobile.querySelector(".cart-rec-mobile__list");
      if (mobileList) {
        var mobileItems = parseRecommendations(html, cartIds, MAX_MOBILE);
        mobileCount = mobileItems.length;
        if (mobileItems.length) {
          fillContainer(mobileList, mobileItems, true);
          mobile.hidden = false;
          setupMobileNav(mobile);
        } else {
          mobile.hidden = true;
        }
      }
    }

    // Impression event — fire once per drawer-open. Subsequent in-open
    // refreshes (e.g. user adds an item then sees the recs re-render) do
    // not re-emit, so view counts stay clean.
    if (!viewLoggedThisOpen) {
      var surfaceCount = isDesktop() ? desktopCount : mobileCount;
      if (surfaceCount > 0) {
        viewLoggedThisOpen = true;
        track("cart_rec_view", {
          surface: isDesktop() ? "desktop" : "mobile",
          count: surfaceCount,
        });
      }
    }
  }

  /* --- Mobile carousel prev/next arrows --------------------------------- */

  function setupMobileNav(mobile) {
    var list = mobile.querySelector(".cart-rec-mobile__list");
    var prevBtn = mobile.querySelector("[data-rec-mobile-prev]");
    var nextBtn = mobile.querySelector("[data-rec-mobile-next]");
    if (!list || !prevBtn || !nextBtn) return;

    // Page-dots container — created once as a sibling after the viewport,
    // re-rendered (one dot per card) every time cards are filled.
    var viewport = mobile.querySelector(".cart-rec-mobile__viewport");
    var dots = mobile.querySelector(".cart-rec-mobile__dots");
    if (!dots && viewport) {
      dots = document.createElement("div");
      dots.className = "cart-rec-mobile__dots";
      dots.setAttribute("aria-hidden", "true");
      viewport.insertAdjacentElement("afterend", dots);
    }

    function getStep() {
      var firstItem = list.querySelector(".cart-rec__item");
      if (!firstItem) return 200;
      var gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 12;
      return firstItem.getBoundingClientRect().width + gap;
    }

    function renderDots() {
      if (!dots) return;
      var cards = list.querySelectorAll(".cart-rec__item");
      var hideDots = cards.length < 2 || list.scrollWidth <= list.clientWidth + 4;
      if (hideDots) {
        dots.hidden = true;
        dots.innerHTML = "";
        return;
      }
      dots.hidden = false;
      // Only rebuild if dot count is out of sync
      if (dots.children.length !== cards.length) {
        dots.innerHTML = "";
        for (var i = 0; i < cards.length; i++) {
          var dot = document.createElement("button");
          dot.type = "button";
          dot.className = "cart-rec-mobile__dot";
          dot.setAttribute("aria-label", "Go to recommendation " + (i + 1));
          dot.dataset.index = String(i);
          dots.appendChild(dot);
        }
      }
    }

    function updateActiveDot() {
      if (!dots || dots.hidden) return;
      var step = getStep();
      var activeIndex = step > 0 ? Math.round(list.scrollLeft / step) : 0;
      Array.prototype.forEach.call(dots.children, function (dot, i) {
        dot.classList.toggle("is-active", i === activeIndex);
      });
    }

    function updateNavState() {
      // No scrollable content → both arrows hidden
      if (list.scrollWidth <= list.clientWidth + 4) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        renderDots();
        return;
      }
      prevBtn.disabled = list.scrollLeft <= 4;
      nextBtn.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
      renderDots();
      updateActiveDot();
    }

    function scrollByCard(direction) {
      list.scrollBy({ left: direction * getStep(), behavior: "smooth" });
    }

    // Bind handlers exactly once per element (placeholder is reused across refreshes)
    if (!prevBtn.dataset.bound) {
      prevBtn.addEventListener("click", function () { scrollByCard(-1); });
      nextBtn.addEventListener("click", function () { scrollByCard(1); });
      list.addEventListener("scroll", function () {
        updateNavState();
      }, { passive: true });
      window.addEventListener("resize", updateNavState);
      prevBtn.dataset.bound = "true";
      nextBtn.dataset.bound = "true";
    }

    // Delegated dot click — bound once on the dots container
    if (dots && !dots.dataset.bound) {
      dots.addEventListener("click", function (e) {
        var dot = e.target.closest(".cart-rec-mobile__dot");
        if (!dot) return;
        var idx = parseInt(dot.dataset.index, 10);
        if (isNaN(idx)) return;
        list.scrollTo({ left: idx * getStep(), behavior: "smooth" });
      });
      dots.dataset.bound = "true";
    }

    // Defer one frame so freshly-injected items have layout
    requestAnimationFrame(updateNavState);
  }

  /* --- card state -------------------------------------------------------- */

  function closeAllPickersExcept(activeCard) {
    document.querySelectorAll(".cart-rec-card.is-picking").forEach(function (card) {
      if (card === activeCard) return;
      closePicker(card);
    });
  }

  function openPicker(card) {
    closeAllPickersExcept(card);
    card.classList.add("is-picking");
  }

  function closePicker(card) {
    card.classList.remove("is-picking");
    // Clear any leftover spinner on size buttons
    card.querySelectorAll(".cart-rec-card__size-btn.is-loading").forEach(function (b) {
      b.classList.remove("is-loading");
      b.disabled = false;
      b.removeAttribute("aria-busy");
    });
  }

  function performAdd(card, sizeBtn) {
    if (sizeBtn.getAttribute("aria-disabled") === "true") return;
    if (sizeBtn.classList.contains("is-loading")) return;

    var variantId = sizeBtn.dataset.variantId;
    if (!variantId) return;

    if (typeof window.maceooAddToCart !== "function") {
      console.warn("[CartRec] window.maceooAddToCart unavailable — cannot add");
      return;
    }

    var meta = getCardMeta(card);
    var sizeValue = sizeBtn.dataset.sizeValue || (sizeBtn.textContent || "").trim();

    // Engagement event — user picked a size (intent + variant context).
    track("cart_rec_size_click", Object.assign({}, meta, {
      variant_id: variantId,
      size_value: sizeValue,
    }));

    sizeBtn.classList.add("is-loading");
    sizeBtn.disabled = true;
    sizeBtn.setAttribute("aria-busy", "true");

    window.maceooAddToCart(variantId)
      .then(function () {
        // Conversion event — the size add succeeded. Also tag the Clarity
        // session so converters are filterable in recordings.
        track("cart_rec_added", Object.assign({}, meta, {
          variant_id: variantId,
          size_value: sizeValue,
        }));
        if (typeof window.maceooClaritySet === "function") {
          window.maceooClaritySet("cart_rec_purchased", "true");
        }

        // Cart drawer refresh will fire — closePicker happens via the
        // refreshed handler too. Close here for snappy local feedback.
        closePicker(card);
      })
      .catch(function (error) {
        sizeBtn.classList.remove("is-loading");
        sizeBtn.disabled = false;
        sizeBtn.removeAttribute("aria-busy");

        track("cart_rec_error", Object.assign({}, meta, {
          variant_id: variantId,
          size_value: sizeValue,
          error_message: (error && error.message) || "unknown",
        }));

        if (typeof window.maceooShowToast === "function") {
          window.maceooShowToast(
            (error && error.message) || "Could not add to cart",
            true,
          );
        }
      });
  }

  /* --- delegated handlers ----------------------------------------------- */

  document.addEventListener("click", function (e) {
    // Close button takes priority — works regardless of card/picker state
    if (e.target.closest("[data-rec-picker-close]")) {
      e.preventDefault();
      e.stopPropagation();
      var card = e.target.closest(".cart-rec-card");
      if (card) closePicker(card);
      return;
    }

    // Size selection inside an open picker
    var sizeBtn = e.target.closest("[data-rec-size]");
    if (sizeBtn) {
      e.preventDefault();
      e.stopPropagation();
      var sizeCard = e.target.closest(".cart-rec-card");
      if (sizeCard) performAdd(sizeCard, sizeBtn);
      return;
    }

    // Tap on Add to Cart toggles the picker for that card
    if (e.target.closest("[data-rec-atc]")) {
      e.preventDefault();
      e.stopPropagation();
      var atcCard = e.target.closest(".cart-rec-card");
      if (!atcCard) return;
      if (atcCard.classList.contains("is-picking")) {
        closePicker(atcCard);
      } else {
        // Intent event — track only when the picker is being OPENED, not on
        // the toggle-close. This is the conversion-funnel entry point.
        track("cart_rec_atc_click", getCardMeta(atcCard));
        openPicker(atcCard);
      }
      return;
    }

    // Any other click outside an open picker closes it. This covers clicks
    // on the image area (still inside the card) AND clicks anywhere else
    // on the page. Clicks INSIDE a picker (background, header) do nothing.
    if (!e.target.closest("[data-rec-picker]")) {
      closeAllPickersExcept(null);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var open = document.querySelector(".cart-rec-card.is-picking");
    if (!open) return;
    closePicker(open);
    var atc = open.querySelector("[data-rec-atc]");
    if (atc) atc.focus();
  });

  /* --- focus-trap pause (panel lives OUTSIDE <cart-drawer>) ------------- */

  var ftPauseTimer = null;

  function pauseFocusTrap() {
    var d = getCartDrawer();
    if (!d || !d.open) return;
    clearTimeout(ftPauseTimer);
    try { d.focusTrap.pause(); } catch (_e) {}
  }

  function unpauseFocusTrap() {
    var d = getCartDrawer();
    clearTimeout(ftPauseTimer);
    try { if (d && d.open) d.focusTrap.unpause(); } catch (_e) {}
  }

  /* --- bootstrap --------------------------------------------------------- */

  function getCurrentData() {
    var mobile = getMobileContainer();
    var bootstrap = window.maceooCartRecData || {};

    var sourceId   = (mobile && mobile.dataset.sourceProduct) || bootstrap.sourceId || "";
    var sourceType = (mobile && mobile.dataset.sourceType)    || bootstrap.sourceType || "";
    var rawIds     = (mobile && mobile.dataset.cartIds)       || "";
    var cartIds    = rawIds ? rawIds.split(",").filter(Boolean) : (bootstrap.cartIds || []);

    if (sourceId) {
      window.maceooCartRecData = { sourceId: sourceId, cartIds: cartIds, sourceType: sourceType };
    }
    return { sourceId: sourceId, cartIds: cartIds, sourceType: sourceType };
  }

  /* --- Dynamic heading copy ---------------------------------------------
   * Maps the first cart item's product type to a more contextual headline.
   * When no rule matches, the original configured copy is preserved (the
   * very first invocation snapshots it onto data-rec-default). */
  function pickHeading(productType) {
    var t = (productType || "").toLowerCase();
    if (!t) return null;
    if (t.indexOf("shirt") !== -1)  return "Complete the look";
    if (t.indexOf("blazer") !== -1) return "Pairs with this blazer";
    if (t.indexOf("polo") !== -1)   return "Style this polo";
    if (t.indexOf("jacket") !== -1) return "Layer with this jacket";
    if (t.indexOf("swim") !== -1 || t.indexOf("short") !== -1) return "Pack the look";
    if (t.indexOf("tee") !== -1 || t.indexOf("t-shirt") !== -1) return "Wear it with";
    return null;
  }

  function applyDynamicHeading(sourceType) {
    var nodes = document.querySelectorAll("[data-rec-heading]");
    if (!nodes.length) return;
    var override = pickHeading(sourceType);
    Array.prototype.forEach.call(nodes, function (node) {
      if (!node.dataset.recDefault) node.dataset.recDefault = node.textContent.trim();
      node.textContent = override || node.dataset.recDefault;
    });
  }

  function showSkeletonIfEmpty() {
    var list = getDesktopList();
    if (!list) return;
    if (list.children.length && !list.querySelector(".cart-rec-loading")) return;
    list.innerHTML =
      '<div class="cart-rec-loading" aria-hidden="true">' +
        '<div class="cart-rec-skeleton"></div>' +
        '<div class="cart-rec-skeleton"></div>' +
        '<div class="cart-rec-skeleton"></div>' +
      "</div>";
  }

  function loadAndRender(sourceId, cartIds) {
    if (!sourceId) return;
    fetchRecommendations(sourceId)
      .then(function (html) { renderRecommendations(html, cartIds); })
      .catch(function () { /* recs are non-critical */ });
  }

  function prefetch() {
    var data = window.maceooCartRecData;
    if (!data || !data.sourceId)  return;
    if (readCache(data.sourceId)) return;
    fetchRecommendations(data.sourceId).catch(function () {});
  }

  if ("requestIdleCallback" in window) {
    requestIdleCallback(prefetch, { timeout: 2500 });
  } else {
    setTimeout(prefetch, 1500);
  }

  /* --- cart-drawer event wiring ----------------------------------------- */

  function init() {
    var drawer = getCartDrawer();
    var panel  = getPanel();
    if (!drawer || !panel) return;

    // Apply dynamic heading once on load so the desktop panel matches the cart
    // before the user ever opens the drawer.
    applyDynamicHeading(getCurrentData().sourceType);

    panel.addEventListener("pointerdown", pauseFocusTrap, true);
    panel.addEventListener("pointerup", function () {
      clearTimeout(ftPauseTimer);
      ftPauseTimer = setTimeout(unpauseFocusTrap, 2000);
    }, true);

    drawer.addEventListener("dialog:before-show", function () {
      // New drawer open = fresh impression window for analytics.
      viewLoggedThisOpen = false;

      var data = getCurrentData();
      applyDynamicHeading(data.sourceType);

      if (data.sourceId && isDesktop()) panel.classList.add("is-visible");
      if (!data.sourceId) return;

      var cached = readCache(data.sourceId);
      if (cached) {
        renderRecommendations(cached, data.cartIds);
      } else {
        showSkeletonIfEmpty();
        loadAndRender(data.sourceId, data.cartIds);
      }
    });

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === "open" && !drawer.hasAttribute("open")) {
          panel.classList.remove("is-visible");
          break;
        }
      }
    }).observe(drawer, { attributes: true, attributeFilter: ["open"] });

    drawer.addEventListener("cart-drawer:refreshed", function () {
      unpauseFocusTrap();

      var data = getCurrentData();
      applyDynamicHeading(data.sourceType);

      var hasItems = !!getMobileContainer();

      if (!hasItems) {
        panel.setAttribute("hidden", "");
        panel.classList.remove("is-visible");
        return;
      }

      panel.removeAttribute("hidden");
      if (drawer.hasAttribute("open") && isDesktop()) panel.classList.add("is-visible");

      if (!data.sourceId) return;
      var cached = readCache(data.sourceId);
      if (cached) renderRecommendations(cached, data.cartIds);
      else        loadAndRender(data.sourceId, data.cartIds);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
